/**
 * WFA Job Adapter
 * 
 * Maps WFA (Walk-Forward Analysis) events and data to the unified Jobs system.
 * Enables WFA jobs to appear in the JobsPanel and JobsConsole.
 */

import type { Job, JobStatus, JobProgress, JobTiming, JobOutput, JobEntityRef } from '../../types/jobs';

/**
 * WFA event types from SSE stream
 */
export type WFAEventType = 
    | 'wfa-queued'
    | 'wfa-progress'
    | 'wfa-completed'
    | 'wfa-error'
    | 'wfa-cancelled';

/**
 * WFA progress data from SSE events
 */
export interface WFAProgressData {
    currentPeriod?: number;
    totalPeriods?: number;
    progress?: number;
    currentPhase?: string;
    error?: string;
    savedWfaId?: string;
    summary?: {
        totalPeriods?: number;
        successfulPeriods?: number;
        avgOutSampleReturn?: number;
        avgOutSampleSharpe?: number;
    };
}

/**
 * WFA event from SSE stream
 */
export interface WFAEvent {
    type: WFAEventType;
    jobId: string;
    data: WFAProgressData;
}

/**
 * Parameters for creating a WFA job
 */
export interface CreateWFAJobParams {
    jobId: string;
    symbol: string;
    interval: string;
    strategyName?: string;
    totalPeriods?: number;
}

/**
 * Extended parameters including configuration details for console logging
 */
export interface WFAJobRegistrationParams extends Omit<CreateWFAJobParams, 'jobId'> {
    // Selection info
    selectionMode?: string;
    includedCount?: number;
    totalCount?: number;
    // WFA settings
    ratio?: string;
    rollingWindows?: boolean;
    // Window configuration
    trainingCandles?: number;
    testingCandles?: number;
    periodLength?: number;
    stepCandles?: number;
    stepSizeMode?: 'auto' | 'manual';
    // Trade estimates
    estimatedTestingTrades?: number;
    tradeEstimateConfidence?: string;
    tradeEstimateSource?: string;
    warning?: string;
}

/**
 * Create a new Job from WFA parameters
 */
export function createJobFromWFA(params: CreateWFAJobParams): Job {
    const progress: JobProgress = {
        current: 0,
        total: params.totalPeriods ?? 0,
        percent: 0,
        phase: 'Queued',
    };

    const timing: JobTiming = {
        createdAt: new Date().toISOString(),
    };

    return {
        id: params.jobId,
        type: 'WFA_RUN',
        status: 'QUEUED',
        context: {
            symbol: params.symbol,
            timeframe: params.interval,
            strategyName: params.strategyName,
        },
        progress,
        timing,
    };
}

/**
 * Job update from a WFA event
 */
export interface WFAJobUpdate {
    status?: JobStatus;
    progress?: Partial<JobProgress>;
    timing?: Partial<JobTiming>;
    output?: JobOutput;
}

/**
 * Map a WFA event to a job update
 */
export function mapWFAEventToJobUpdate(event: WFAEvent): WFAJobUpdate {
    const update: WFAJobUpdate = {};

    switch (event.type) {
        case 'wfa-queued':
            update.status = 'QUEUED';
            update.progress = { phase: 'Queued' };
            break;

        case 'wfa-progress':
            update.status = 'RUNNING';
            update.progress = {
                current: event.data.currentPeriod ?? 0,
                total: event.data.totalPeriods ?? 0,
                percent: event.data.progress ?? 0,
                phase: event.data.currentPhase ?? 'Processing',
            };
            update.timing = { startedAt: new Date().toISOString() };
            break;

        case 'wfa-completed':
            update.status = 'COMPLETED';
            update.progress = {
                percent: 100,
                phase: 'Complete',
            };
            update.timing = { endedAt: new Date().toISOString() };
            
            // If there's a saved WFA ID, add it as an artifact reference
            if (event.data.savedWfaId) {
                const artifactRef: JobEntityRef = {
                    kind: 'WFA_REPORT',
                    id: event.data.savedWfaId,
                };
                update.output = {
                    artifactRefs: [artifactRef],
                    partial: false,
                };
            }
            break;

        case 'wfa-error':
            update.status = 'FAILED';
            update.progress = { phase: 'Failed' };
            update.timing = { endedAt: new Date().toISOString() };
            break;

        case 'wfa-cancelled':
            update.status = 'CANCELLED';
            update.progress = { phase: 'Cancelled' };
            update.timing = { endedAt: new Date().toISOString() };
            break;
    }

    return update;
}
